import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../../constants/numbers.ts';
import IResponse, { IPagination } from '../../../interfaces/response.ts';
import { useDebounce } from 'use-debounce';
import BaranDataGrid from '../../common/BaranDataGrid.tsx';
import axios from 'axios';
import { IBlogs } from '../../../interfaces/IBlogs.ts';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BasicModal from '../../common/BasicModal';
import { LoadingButton } from '@mui/lab';
import EditIcon from '@mui/icons-material/Edit';

export default function Blogs() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [fetchedData, setFetchedData] = useState<IBlogs[]>([]);
	const { t } = useTranslation();
	const [deleteNews, setDeleteNews] = useState<IBlogs>();
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [refresh, setRefresh] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const headerClassNames = 'text-xs bg-background-paper';
	const navigate = useNavigate();

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};
	const handleViewNews = (newsId: string) => () => {
		navigate(`/admin/blog/${newsId}/edit`);
	};
	const createNewNews = () => {
		navigate(`/admin/blog/create`);
	};
	const handleCloseDeleteDialog = () => {
		setDeleteNews(undefined);
	};

	const handleCloseDelete = (news: IBlogs) => () => {
		setDeleteNews(news);
	};

	const handleDeleteNews = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: `/admin/posts/${deleteNews?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteNews(undefined);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
				setRefresh(prev => prev + 1);
			});
	};

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IBlogs[]>>({
			url: '/admin/posts',
			params: {
				page,
				limit: pageSize,
				search: JSON.stringify([{ field: 'title', value: search }]),
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, search, refresh]);

	const columns: GridColDef<IBlogs>[] = [
		{
			field: 'title',
			headerName: t('title'),
			width: 200,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('Creation-date'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'is_active',
			headerName: t('status'),
			width: 160,
			headerAlign: 'left',
			headerClassName: `${headerClassNames} ps-12`,
			align: 'left',
			renderCell: params => {
				const statusText = params.value ? t('active') : t('inactive');
				const bgColorClass = params.value ? 'bg-success-selected text-success-main' : 'bg-error-selected text-error-main';
				return <div className={`ms-10 mt-4 flex w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{statusText}</div>;
			},
		},

		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton onClick={handleViewNews(params.row.id.toString())}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleCloseDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
						<IconButton aria-label="eye" onClick={handleViewNews(params.row.id.toString())}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];
	return (
		<div className={'w-full rounded'}>
			<BaranDataGrid<IBlogs> loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} searchTerm={searchTerm} handleSearch={handleSearch} setPageSize={setPageSize} page={page} setPage={setPage} onClick={createNewNews} actionButtonLable={t('create_new_blog')} />;
			<BasicModal
				open={!!deleteNews}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteNews} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_news_description')}</Typography>
			</BasicModal>
		</div>
	);
}
